/**
 * Ranking and Classification Utilities
 * Contains threshold data and helper functions for ELO-based rankings
 */

// Classification thresholds for male players
export const MALE_CLASSIFICATION_THRESHOLDS = [
  { class: 'A22', threshold: 1990 },
  { class: 'A21', threshold: 1890 },
  { class: 'A20', threshold: 1790 },
  { class: 'A19', threshold: 1680 },
  { class: 'A18', threshold: 1565 },
  { class: 'A17', threshold: 1490 },
  { class: 'A16', threshold: 1435 },
  { class: 'B15', threshold: 1360 },
  { class: 'B14', threshold: 1320 },
  { class: 'B13', threshold: 1280 },
  { class: 'B12', threshold: 1240 },
  { class: 'B11', threshold: 1200 },
  { class: 'C10', threshold: 1150 },
  { class: 'C9', threshold: 1100 },
  { class: 'C8', threshold: 1050 },
  { class: 'C7', threshold: 990 },
  { class: 'C6', threshold: 930 },
  { class: 'D5', threshold: 860 },
  { class: 'D4', threshold: 780 },
  { class: 'D3', threshold: 700 },
  { class: 'D2', threshold: 630 },
  { class: 'D1', threshold: 0 }
];

// Classification thresholds for female players
export const FEMALE_CLASSIFICATION_THRESHOLDS = [
  { class: 'A22', threshold: 1790 },
  { class: 'A21', threshold: 1680 },
  { class: 'A20', threshold: 1435 },
  { class: 'A19', threshold: 1360 },
  { class: 'A18', threshold: 1280 },
  { class: 'A17', threshold: 1240 },
  { class: 'A16', threshold: 1200 },
  { class: 'B15', threshold: 1150 },
  { class: 'B14', threshold: 1100 },
  { class: 'B13', threshold: 1050 },
  { class: 'B12', threshold: 990 },
  { class: 'B11', threshold: 930 },
  { class: 'C10', threshold: 890 },
  { class: 'C9', threshold: 840 },
  { class: 'C8', threshold: 790 },
  { class: 'C7', threshold: 740 },
  { class: 'C6', threshold: 700 },
  { class: 'D5', threshold: 675 },
  { class: 'D4', threshold: 650 },
  { class: 'D3', threshold: 630 },
  { class: 'D2', threshold: 615 },
  { class: 'D1', threshold: 0 }
];

/**
 * Get classification thresholds based on gender
 */
export const getClassificationThresholds = (isFemale: boolean) => {
  return isFemale ? FEMALE_CLASSIFICATION_THRESHOLDS : MALE_CLASSIFICATION_THRESHOLDS;
};

/**
 * Get rank/class from ELO rating
 */
export const getRankFromElo = (elo: number, gender: string = 'M'): string => {
  const isFemale = gender === 'F';
  const rankings = getClassificationThresholds(isFemale);

  for (const r of rankings) {
    if (elo >= r.threshold) {
      return r.class;
    }
  }
  return 'D1';
};

/**
 * Get the next classification threshold (for progress tracking)
 */
export const getNextClassificationThreshold = (
  currentClass: string,
  isFemale: boolean
): { class: string; threshold: number } | null => {
  const thresholds = getClassificationThresholds(isFemale);
  const currentIndex = thresholds.findIndex(t => t.class === currentClass);

  if (currentIndex === -1 || currentIndex === 0) return null;
  return thresholds[currentIndex - 1];
};

/**
 * Get the threshold value for a specific classification
 */
export const getCurrentClassificationThreshold = (
  classification: string,
  isFemale: boolean
): number => {
  const thresholds = getClassificationThresholds(isFemale);
  const found = thresholds.find(t => t.class === classification);
  return found ? found.threshold : 0;
};
